import React, { useState, useEffect } from 'react'
import { useTournaments } from '../../contexts/TournamentContext'
import { useAuth } from '../../contexts/AuthContext'
import { apiUrl } from '../../config/api'
import { showSuccess, showError } from '../Toast/Toast'
import QuestAccountModal from '../Modal/QuestAccountModal'
import InviteCodeModal from '../Modal/InviteCodeModal'
import PremiumTournamentModal from '../Modal/PremiumTournamentModal'
import VerifyEmailModal from '../Modal/VerifyEmailModal'
import './Tournaments.css'

const Tournaments = ({ onShowTournamentDetails, initialTab = 'big', onNavigateToAuth }) => {
  const { user, profile, isGuest, isVerified } = useAuth()
  const {
    tournaments,
    loading,
    error,
    listTournaments,
    joinTournament,
    leaveTournament,
    getTournamentStandings,
    clearError
  } = useTournaments()

  const [activeTab, setActiveTab] = useState(initialTab)
  const [filterStatus, setFilterStatus] = useState('registering')
  const [currentTime, setCurrentTime] = useState(new Date())
  const [showQuestModal, setShowQuestModal] = useState(false)
  const [tournamentHistory, setTournamentHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Invite Code State
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState(null)

  // Premium Tournament Modal State
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [userPremiumStatus, setUserPremiumStatus] = useState(
    (user?.account_type === 'premium') || (profile?.account_type === 'premium')
  )

  // Verify Email Modal State
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false)

  useEffect(() => {
    if (activeTab === 'history') {
      loadTournamentHistory()
    } else {
      listTournaments(filterStatus)
    }
  }, [listTournaments, filterStatus, activeTab])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  // Update countdown timer every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [])

  // Check premium status when component mounts or user changes
  useEffect(() => {
    const hasPremiumAccount = (user?.account_type === 'premium') || (profile?.account_type === 'premium')
    setUserPremiumStatus(hasPremiumAccount)
  }, [user?.account_type, profile?.account_type])

  const handleJoinTournament = (tournamentId) => {
    // Check if user is a guest account
    if (isGuest) {
      setShowQuestModal(true)
      return
    }

    // Check if user email is verified
    if (!isVerified) {
      setShowVerifyEmailModal(true)
      return
    }

    // Check if tournament is invite-only
    const tournament = tournaments.find(t => t.id === tournamentId)
    const isInviteOnly = tournament && tournament.access_type === 'invite_only'

    // Check if user has premium - required for public tournaments only
    if (!userPremiumStatus && !isInviteOnly) {
      setShowPremiumModal(true)
      return
    }

    if (isInviteOnly) {
      // Open invite code modal
      setSelectedTournament(tournament)
      setShowInviteModal(true)
      return
    }

    // Regular join
    performJoin(tournamentId, null)
  }

  const performJoin = async (tournamentId, code) => {
    // Find tournament to check access type
    const tournament = tournaments.find(t => t.id === tournamentId)
    const isInviteOnly = tournament && tournament.access_type === 'invite_only'

    // Check if user has premium - required for public tournaments
    if (!userPremiumStatus && !isInviteOnly) {
      setShowInviteModal(false)
      setSelectedTournament(null)
      setShowPremiumModal(true)
      return
    }

    try {
      await joinTournament(tournamentId, code)
      showSuccess('Successfully joined tournament!')
      setShowInviteModal(false)
      setSelectedTournament(null)
    } catch (err) {
      console.error('Failed to join tournament:', err)
      showError(err.message || 'Failed to join tournament. Please try again.')
    }
  }

  const handleLeaveTournament = async (tournamentId) => {
    try {
      await leaveTournament(tournamentId)
      showSuccess('Successfully left tournament!')
    } catch (err) {
      console.error('Failed to leave tournament:', err)
      showError('Failed to leave tournament. Please try again.')
    }
  }


  const loadTournamentHistory = async () => {
    setLoadingHistory(true)
    try {
      // Fetch completed tournaments with their standings
      const completedTournaments = await listTournaments('completed')


      // For each tournament, get its standings
      const tournamentsWithStandings = await Promise.all(
        completedTournaments.tournaments?.map(async (tournament) => {
          try {
            const standings = await getTournamentStandings(tournament.id)
            return {
              ...tournament,
              standings: standings || []
            }
          } catch (err) {
            console.error('Failed to load standings for tournament:', tournament.id)
            return {
              ...tournament,
              standings: []
            }
          }
        }) || []
      )

      // Filter out test tournaments from history
      const filteredHistory = tournamentsWithStandings.filter(t => !t.name.toLowerCase().includes('test'))
      setTournamentHistory(filteredHistory)
    } catch (err) {
      console.error('Failed to load tournament history:', err)
      setTournamentHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const formatTimeControl = (timeControl) => {
    if (!timeControl) return 'Classic'
    const { initial_time, increment } = timeControl
    if (!initial_time && !increment) return 'Classic'
    const minutes = Math.floor(initial_time / 60)
    return `${minutes}+${increment}`
  }

  const formatCountdown = (startTime) => {
    if (!startTime) return '';

    const now = new Date();
    const start = new Date(startTime);
    const diff = start - now;

    if (diff <= 0) return 'Starting soon';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  const formatPlayerCount = (tournament) => {
    const { players_count, max_players } = tournament
    return max_players > 0 ? `${players_count}/${max_players}` : players_count.toString()
  }

  const getTournamentStatus = (tournament) => {
    const now = new Date()
    const startTime = tournament.start_time ? new Date(tournament.start_time) : null
    const name = tournament.name.toLowerCase()

    // Check if this is a weekly or daily tournament
    const isWeeklyOrDaily = name.includes('weekly') || name.includes('daily')

    if (isWeeklyOrDaily) {
      return { text: 'COMING SOON', class: 'coming-soon' }
    }

    if (tournament.status === 'registering') {
      if (tournament.max_players > 0 && tournament.players_count >= tournament.max_players) {
        return { text: 'FULL', class: 'full' }
      }
      if (startTime && now >= startTime) {
        return { text: 'STARTING', class: 'starting' }
      }
      return { text: 'OPEN', class: 'open' }
    }

    return { text: tournament.status.toUpperCase(), class: tournament.status }
  }

  const isUserRegistered = (tournament) => {
    if (!user?.id || !tournament.players) return false
    return tournament.players.some(player => player.id === user.id)
  }

  const handleUpgradeToPremium = () => {
    setShowPremiumModal(false)
    // Navigate to premium page in Dashboard - pass onBack function
    if (onNavigateToAuth) {
      onNavigateToAuth('premium')
    }
  }

  const handleCreateAccount = () => {
    setShowQuestModal(false)
    // Navigate to auth page
    if (onNavigateToAuth) {
      onNavigateToAuth('auth')
    }
  }


  const filterTournaments = (type) => {
    // Filter out test tournaments
    const filteredTournaments = tournaments.filter(t => !t.name.toLowerCase().includes('test'))

    if (type === 'big') {
      const bigTournaments = filteredTournaments.filter(t => !t.max_players || t.max_players > 32)
      // Sort tournaments by priority: Invitational first, Monthly second, Weekly third, Daily fourth, then others
      return bigTournaments.sort((a, b) => {
        const getOrderPriority = (tournament) => {
          const name = tournament.name.toLowerCase()
          if (name.includes('invitational')) return 1
          if (name.includes('monthly')) return 2
          if (name.includes('weekly')) return 3
          if (name.includes('daily')) return 4
          return 5
        }
        return getOrderPriority(a) - getOrderPriority(b)
      })
    }
    return filteredTournaments.filter(t => t.max_players && t.max_players <= 32)
  }

  const calculatePrizePool = (tournament) => {
    const entryFee = tournament.prize_pool?.entry_fee || 5;
    const maxPlayers = tournament.max_players || 8;
    const totalEntry = entryFee * maxPlayers;
    const platformFee = totalEntry * 0.03; // 3% platform fee
    const prizePool = totalEntry - platformFee;
    return Math.round(prizePool);
  };

  const getBigTournamentMinimum = (tournament) => {
    // Determine tournament type based on name or other properties
    const name = tournament.name.toLowerCase();
    if (name.includes('monthly')) {
      return tournament.prize_pool?.total_amount || 1000;
    } else if (name.includes('weekly')) {
      return tournament.prize_pool?.total_amount || 200;
    } else if (name.includes('daily')) {
      return tournament.prize_pool?.total_amount || 30;
    }
    return 100; // default for big tournaments
  };

  const getScheduledStartTime = (tournament) => {
    const name = tournament.name.toLowerCase();
    const now = new Date();

    if (name.includes('monthly')) {
      // January 31st, 2026 at 18:00 CET
      const januaryTournament = new Date('2026-01-31T18:00:00+01:00'); // CET timezone
      return januaryTournament;
    } else if (name.includes('weekly')) {
      // Next Saturday at 18:00 CET
      const nextSaturday = new Date(now);
      nextSaturday.setDate(now.getDate() + (6 - now.getDay() + 7) % 7);
      nextSaturday.setHours(18, 0, 0, 0);
      if (nextSaturday <= now) {
        nextSaturday.setDate(nextSaturday.getDate() + 7);
      }
      return nextSaturday;
    }

    return tournament.start_time ? new Date(tournament.start_time) : null;
  };

  const getPrizePoolDisplay = (tournament) => {
    return tournament.prize_pool?.total_amount ?
      `$${tournament.prize_pool.total_amount}` : "TBD";
  };

  const renderTournamentCard = (tournament, fromTab = 'big') => {
    const status = getTournamentStatus(tournament)
    const isCreator = tournament.creator?.id === user?.id
    const isRegistered = isUserRegistered(tournament)
    const prizePoolDisplay = getPrizePoolDisplay(tournament);
    const isBigTournament = (!tournament.max_players || tournament.max_players > 32);
    const startTime = isBigTournament ? getScheduledStartTime(tournament) : (tournament.start_time ? new Date(tournament.start_time) : null);
    const name = tournament.name.toLowerCase()
    const isWeeklyOrDaily = name.includes('weekly') || name.includes('daily')

    const isComingSoon = fromTab === 'sitgo';

    if (isComingSoon || isWeeklyOrDaily) {
      return (
        <div key={tournament.id} className="sitgo-coming-soon-wrapper">
          <div className={`tournament-card ${isWeeklyOrDaily ? 'disabled-tournament' : ''}`}>
            {/* Tournament card content */}
            <div className="tournament-top-row">
              <span
                className="tournament-entry"
                style={{
                  color: isWeeklyOrDaily ? '#666' : (tournament.name.toLowerCase().includes('invitational') ? '' : '#888')
                }}
              >
                {tournament.name.toLowerCase().includes('invitational') ? 'Invite Only' : 'Free entry'}
              </span>
              <div className={`tournament-status ${status.class}`}>
                {status.text}
              </div>
            </div>

            {isBigTournament && (
              <>
                <div className="tournament-prize-row">
                  <span className="prize-label">PRIZE POOL:</span>
                  <span className="prize-value-inline">{prizePoolDisplay}</span>
                </div>
                {startTime && (
                  <div className="tournament-countdown-row">
                    <span className="countdown-label">STARTS IN:</span>
                    <span className="countdown-value">{formatCountdown(startTime)}</span>
                  </div>
                )}
              </>
            )}

            <div className="tournament-header">
              <div className="tournament-info">
                <h3 className="tournament-name">{tournament.name.replace(/\s*\(bracket\)|\s*\(round_robin\)|\s*Sit & Go/gi, '')}</h3>
                <p className="tournament-description">{isBigTournament ? 'Join the competition!' : 'Starts when full!'}</p>
                <div className="tournament-meta">
                  {isBigTournament ? (
                    <div className="tournament-meta-horizontal">
                      <span className="tournament-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                      <span className="tournament-time">{formatTimeControl(tournament.time_control)}</span>
                    </div>
                  ) : (
                    <>
                      <span className="tournament-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                      <span className="tournament-time">{formatTimeControl(tournament.time_control)}</span>
                    </>
                  )}
                </div>
              </div>
              {!isBigTournament && (
                <div className="tournament-prize">
                  <div className="prize-label">PRIZE POOL</div>
                  <div className="prize-value">{prizePoolDisplay}</div>
                </div>
              )}
            </div>

            <div className="tournament-details">
              <div className={`detail-row ${isBigTournament ? 'detail-row-separated' : ''}`}>
                <span className="detail-label">{isBigTournament ? 'Registered Players:' : 'Players:'}</span>
                <span className="detail-value">{formatPlayerCount(tournament)}</span>
              </div>

              {startTime && (
                <div className="detail-row">
                  <span className={`detail-label ${isBigTournament ? 'start-date-label' : ''}`}>Start Date:</span>
                  <span className={`detail-value ${isBigTournament ? 'start-date-value' : ''}`}>
                    {isBigTournament ? (
                      <>
                        {startTime.toLocaleDateString('en-GB', {timeZone: 'Europe/Berlin'})} {startTime.toLocaleTimeString('en-GB', {timeZone: 'Europe/Berlin', hour12: false})} CET
                      </>
                    ) : (
                      startTime.toLocaleString('en-GB', {timeZone: 'Europe/Berlin', hour12: false}) + ' CET'
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="tournament-actions">
              {tournament.status === 'registering' && !isCreator && (
                <>
                  {isRegistered ? (
                    <button
                      className="leave-btn"
                      onClick={() => handleLeaveTournament(tournament.id)}
                      disabled={loading}
                    >
                      Unregister
                    </button>
                  ) : (
                    <button
                      className="join-btn"
                      onClick={() => handleJoinTournament(tournament.id)}
                      disabled={loading || (tournament.max_players > 0 && tournament.players_count >= tournament.max_players)}
                    >
                      {tournament.max_players > 0 && tournament.players_count >= tournament.max_players
                        ? 'Full'
                        : 'Register'
                      }
                    </button>
                  )}
                  <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                    Details
                  </button>
                </>
              )}

              {isCreator && (
                <>
                  <span className="creator-badge">Your Tournament</span>
                  <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                    Details
                  </button>
                </>
              )}

              {tournament.status === 'active' && (
                <>
                  <button className="spectate-btn">
                    Spectate
                  </button>
                  <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                    Details
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Coming Soon Overlay */}
          <div className="sitgo-coming-soon-overlay">
            <span className="sitgo-coming-soon-text">COMING SOON</span>
          </div>
        </div>
      )
    }

    return (
      <div key={tournament.id} className={`tournament-card ${isWeeklyOrDaily ? 'disabled-tournament' : ''}`}>
        <div className="tournament-top-row">
          <span
            className="tournament-entry"
            style={{
              color: isWeeklyOrDaily ? '#666' : (tournament.name.toLowerCase().includes('invitational') ? '' : '#888')
            }}
          >
            {tournament.name.toLowerCase().includes('invitational') ? 'Invite Only' : 'Free entry'}
          </span>
          <div className={`tournament-status ${status.class}`}>
            {status.text}
          </div>
        </div>

        {isBigTournament && (
          <>
            <div className="tournament-prize-row">
              <span className={`prize-label ${isWeeklyOrDaily ? 'disabled-badge' : ''}`}>PRIZE POOL:</span>
              <span className={`prize-value-inline ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{prizePoolDisplay}</span>
            </div>
            {startTime && (
              <div className="tournament-countdown-row">
                <span className={`countdown-label ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>STARTS IN:</span>
                <span className={`countdown-value ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{formatCountdown(startTime)}</span>
              </div>
            )}
          </>
        )}

        <div className="tournament-header">
          <div className="tournament-info">
            <h3 className={`tournament-name ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{tournament.name.replace(/\s*\(bracket\)|\s*\(round_robin\)|\s*Sit & Go/gi, '')}</h3>
            <p className={`tournament-description ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{isBigTournament ? 'Join the competition!' : 'Starts when full!'}</p>
            <div className="tournament-meta">
              {isBigTournament ? (
                <div className="tournament-meta-horizontal">
                  <span className={`tournament-type ${isWeeklyOrDaily ? 'disabled-badge' : ''}`}>{tournament.type.replace('_', ' ').toUpperCase()}</span>
                  <span className={`tournament-time ${isWeeklyOrDaily ? 'disabled-badge' : ''}`}>{formatTimeControl(tournament.time_control)}</span>
                </div>
              ) : (
                <>
                  <span className={`tournament-type ${isWeeklyOrDaily ? 'disabled-badge' : ''}`}>{tournament.type.replace('_', ' ').toUpperCase()}</span>
                  <span className={`tournament-time ${isWeeklyOrDaily ? 'disabled-badge' : ''}`}>{formatTimeControl(tournament.time_control)}</span>
                </>
              )}
            </div>
          </div>
          {!isBigTournament && (
            <div className="tournament-prize">
              <div className="prize-label">PRIZE POOL</div>
              <div className="prize-value">{prizePoolDisplay}</div>
            </div>
          )}
        </div>

        <div className="tournament-details">
          <div className={`detail-row ${isBigTournament ? 'detail-row-separated' : ''}`}>
            <span className={`detail-label ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{isBigTournament ? 'Registered Players:' : 'Players:'}</span>
            <span className={`detail-value ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>{formatPlayerCount(tournament)}</span>
          </div>

          {startTime && (
            <div className="detail-row">
              <span className={`detail-label ${isBigTournament ? 'start-date-label' : ''} ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>Start Date:</span>
              <span className={`detail-value ${isBigTournament ? 'start-date-value' : ''} ${isWeeklyOrDaily ? 'disabled-text' : ''}`}>
                {isBigTournament ? (
                  <>
                    {startTime.toLocaleDateString('en-GB', {timeZone: 'Europe/Berlin'})} {startTime.toLocaleTimeString('en-GB', {timeZone: 'Europe/Berlin', hour12: false})} CET
                  </>
                ) : (
                  startTime.toLocaleString('en-GB', {timeZone: 'Europe/Berlin', hour12: false}) + ' CET'
                )}
              </span>
            </div>
          )}


        </div>

        <div className="tournament-actions">
          {tournament.status === 'registering' && !isCreator && !isWeeklyOrDaily && (
            <>
              {isRegistered ? (
                <button
                  className="leave-btn"
                  onClick={() => handleLeaveTournament(tournament.id)}
                  disabled={loading}
                >
                  Unregister
                </button>
              ) : (
                <button
                  className="join-btn"
                  onClick={() => handleJoinTournament(tournament.id)}
                  disabled={loading || (tournament.max_players > 0 && tournament.players_count >= tournament.max_players)}
                >
                  {tournament.max_players > 0 && tournament.players_count >= tournament.max_players
                    ? 'Full'
                    : 'Register'
                  }
                </button>
              )}
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}

          {isWeeklyOrDaily && (
            <div className="disabled-actions">
              <span className="disabled-message">Registration opens soon</span>
            </div>
          )}

          {isCreator && (
            <>
              <span className="creator-badge">Your Tournament</span>
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}

          {tournament.status === 'active' && (
            <>
              <button className="spectate-btn">
                Spectate
              </button>
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // Toggle for Test Tab - set to false to hide in production
  const SHOW_TEST_TAB = true

  return (
    <div className="tournaments-page">
      <VerifyEmailModal 
        isOpen={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
      />

      <div className="tournaments-header">
        <h1>Tournaments</h1>
        <p>Join or create chess tournaments with players around the world</p>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>

      <div className="tournaments-tabs">
        <button
          className={`tab-btn ${activeTab === 'big' ? 'active' : ''}`}
          onClick={() => setActiveTab('big')}
        >
          Big Tournaments
        </button>
        <button
          className={`tab-btn ${activeTab === 'sitgo' ? 'active' : ''}`}
          onClick={() => setActiveTab('sitgo')}
        >
          Sit & Go
        </button>
        {SHOW_TEST_TAB && (
          <button
            className={`tab-btn ${activeTab === 'test' ? 'active' : ''}`}
            onClick={() => setActiveTab('test')}
          >
            Test
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className="tournaments-filters">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="registering">Open for Registration</option>
          <option value="active">In Progress</option>
          <option value="completed">Completed</option>
          <option value="">All Tournaments</option>
        </select>
      </div>

      <div className="tournaments-content">
        {activeTab === 'big' && (
          <div className="big-tournaments">
            <div className="section-header">
              <h2>Big Tournaments</h2>
            </div>

            <div className="tournaments-grid">
              {filterTournaments('big').map(tournament => renderTournamentCard(tournament, 'big'))}

              {filterTournaments('big').length === 0 && !loading && (
                <div className="empty-state">
                  <p>No big tournaments available right now</p>
                  <p>Check back soon for upcoming events!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sitgo' && (
          <div className="sitgo-tournaments">
            <div className="section-header">
              <h2>Sit & Go Tournaments</h2>
              <p>Quick tournaments that start when full - Join anytime!</p>
            </div>


            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Single Elimination Bracket</h3>
                <p>Fast-paced knockout tournaments - Winner takes all!</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'bracket').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'bracket').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No bracket tournaments available</p>
                    <p>Create one to get started!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Round Robin</h3>
                <p>Everyone plays everyone - Most points wins!</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'round_robin').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'round_robin').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No round robin tournaments available</p>
                    <p>Create one to get started!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Swiss & Arena</h3>
                <p>Dynamic pairings and non-elimination formats</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'swiss' || t.type === 'arena').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'swiss' || t.type === 'arena').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No swiss or arena tournaments available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'test' && (
          <div className="test-tournaments">
            <div className="section-header">
              <h2>Test View</h2>
              <p>Showing ALL available tournaments (ignoring player limits)</p>
            </div>
            
            <div className="tournaments-grid">
              {/* Show all loaded tournaments directly without size filtering */}
              {tournaments.map(tournament => renderTournamentCard(tournament, 'test'))}

              {tournaments.length === 0 && !loading && (
                <div className="empty-state">
                  <p>No tournaments found matching current filter ({filterStatus})</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="tournament-history">
            <div className="section-header">
              <h2>Tournament History</h2>
              <p>Complete results and standings from all past tournaments</p>
            </div>

            {loadingHistory ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading tournament history...</p>
              </div>
            ) : (
              <div className="history-list">
                {tournamentHistory.length > 0 ? (
                  tournamentHistory.map(tournament => (
                    <div key={tournament.id} className="history-tournament-card">
                      <div className="history-header">
                        <div className="history-info">
                          <h3 className="history-name">{tournament.name}</h3>
                          <div className="history-meta">
                            <span className="history-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                            <span className="history-time-control">{formatTimeControl(tournament.time_control)}</span>
                            <span className="history-date">
                              {tournament.ended_at ?
                                new Date(tournament.ended_at).toLocaleDateString('en-GB', {
                                  timeZone: 'Europe/Berlin',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : 'Date unknown'
                              }
                            </span>
                          </div>
                        </div>
                        <div className="history-actions">
                          <button
                            className="history-details-btn"
                            onClick={() => onShowTournamentDetails?.(tournament.id, 'history')}
                          >
                            View Details
                          </button>
                        </div>
                      </div>

                      {tournament.standings && tournament.standings.length > 0 && (
                        <div className="history-standings">
                          <h4>Final Standings</h4>
                          <div className="standings-grid">
                            {tournament.standings.slice(0, 6).map((standing, index) => (
                              <div key={standing.id || index} className={`standing-row ${index < 3 ? 'podium' : ''}`}>
                                <span className={`standing-position ${
                                  index === 0 ? 'first' :
                                  index === 1 ? 'second' :
                                  index === 2 ? 'third' : ''
                                }`}>
                                  {index === 0 ? '🥇' :
                                   index === 1 ? '🥈' :
                                   index === 2 ? '🥉' :
                                   `#${index + 1}`}
                                </span>
                                <span className="standing-name">{standing.user?.username || 'Unknown'}</span>
                                <span className="standing-points">{standing.points || 0} pts</span>
                                <div className="standing-record">
                                  <span className="wins">W:{standing.wins || 0}</span>
                                  <span className="draws">D:{standing.draws || 0}</span>
                                  <span className="losses">L:{standing.losses || 0}</span>
                                </div>
                              </div>
                            ))}
                            {tournament.standings.length > 6 && (
                              <div className="standings-more">
                                +{tournament.standings.length - 6} more players
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No completed tournaments found</p>
                    <p>Tournament history will appear here once tournaments are completed</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading tournaments...</p>
          </div>
        )}
      </div>

      <QuestAccountModal
        isOpen={showQuestModal}
        onClose={() => setShowQuestModal(false)}
        onCreateAccount={handleCreateAccount}
      />

      <InviteCodeModal
          isOpen={showInviteModal}
          onClose={() => {
              setShowInviteModal(false)
              setSelectedTournament(null)
          }}
          onConfirm={(code) => performJoin(selectedTournament?.id, code)}
          loading={loading}
      />

      <PremiumTournamentModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        onUpgradeToPremium={handleUpgradeToPremium}
      />
    </div>
  )
}

export default Tournaments
